import { useTranslation } from 'react-i18next'
import {
  durationToneClass,
  failureToneClass,
  formatMs,
  formatPct,
} from '@/features/admin/processing-format'
import type { ProcessingStepMetric } from '@/services/api/adminProcessing'

function Metric({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-muted-foreground">{label}</dt>
      <dd className={`tabular-nums ${tone}`}>{value}</dd>
    </div>
  )
}

/**
 * Phone layout of the step-performance table: the step name on its own line and the four
 * timings under it, so p95 and the failure rate are readable without scrolling sideways.
 */
export function StepsMobileList({ steps }: { steps: ProcessingStepMetric[] }) {
  const { t } = useTranslation()
  if (steps.length === 0) {
    return (
      <div className="px-2 py-2 text-sm text-muted-foreground md:hidden">
        {t('admin.processing.steps.empty')}
      </div>
    )
  }
  return (
    <ul className="divide-y divide-border md:hidden">
      {steps.map(s => (
        <li key={s.name} className="space-y-1.5 px-2 py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 text-sm font-medium wrap-anywhere">{s.name}</span>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {t('admin.processing.steps.colCount')} {s.count}
            </span>
          </div>
          <dl className="grid grid-cols-4 gap-2 text-xs">
            <Metric
              label={t('admin.processing.steps.colAvg')}
              value={formatMs(s.avg_ms)}
              tone={durationToneClass(s.avg_ms)}
            />
            <Metric label="p50" value={formatMs(s.p50_ms)} tone={durationToneClass(s.p50_ms)} />
            <Metric label="p95" value={formatMs(s.p95_ms)} tone={durationToneClass(s.p95_ms)} />
            <Metric
              label={t('admin.processing.steps.colFail')}
              value={formatPct(s.failure_rate)}
              tone={failureToneClass(s.failure_rate)}
            />
          </dl>
        </li>
      ))}
    </ul>
  )
}
